import cn from 'classnames';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  Organization,
  OrganizationContacts,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { getImageSrc } from '@/shared/lib';

import {
  getOrganizationPrintName,
  useCountriesOptions,
} from '@/entities/organization';
import { userModel } from '@/entities/user';

import { usePreviewHeaderLayout } from '../../hooks';

import styles from './PreviewHeader.module.scss';

const getContactsLocation = (contacts?: OrganizationContacts) => {
  const locationParts: string[] = [];

  if (!contacts) {
    return '';
  }

  if (contacts.City) {
    locationParts.push(contacts.City);
  }

  if (contacts.Region) {
    locationParts.push(contacts.Region);
  }

  if (contacts.ZipCode) {
    locationParts.push(contacts.ZipCode);
  }

  return locationParts.join(', ').trim();
};

type PreviewHeaderProps = {
  organization?: Organization;
  blackAndWhiteMode?: boolean;
};

export const PreviewHeader: FC<PreviewHeaderProps> = (props) => {
  const { organization, blackAndWhiteMode } = props;

  const { formatMessage } = useIntl();

  const locale = useAppSelector(userModel.selectors.selectUserLocale);

  const countryOptions = useCountriesOptions(locale);

  const organizationCountry = organization?.Contacts?.Country;

  const logoSrc = getImageSrc(organization?.LogoAssetID, 'optimized');
  const organizationName = getOrganizationPrintName(organization);

  const currentCountry = useMemo(
    () =>
      countryOptions?.find(
        (countryOption) => countryOption.value === organizationCountry,
      )?.label ?? '',
    [countryOptions, organizationCountry],
  );

  const { isStacked, logoWidth, sectionRef, logoRef, titleRef } =
    usePreviewHeaderLayout({ logoSrc, organizationName });

  const location = getContactsLocation(organization?.Contacts);

  return (
    <div className={styles.container} data-component="preview-header">
      <div className={styles.clinicInfo}>
        <div
          ref={sectionRef}
          className={cn(
            styles.leftSection,
            logoSrc && styles.titleWithLogo,
            logoSrc && isStacked && styles.stacked,
          )}
        >
          {logoSrc && (
            <img
              ref={logoRef}
              className={cn(
                styles.orgLogo,
                blackAndWhiteMode && styles.blackOrgLogo,
              )}
              src={logoSrc}
              style={{ width: logoWidth }}
              alt={formatMessage({
                id: 'imgAltText.organizationLogo',
                defaultMessage: 'Organization Logo',
              })}
            />
          )}

          <h5 ref={titleRef} className={cn(styles.clinicTitle, 'h7')}>
            {organizationName}
          </h5>
        </div>

        <div className={styles.rightSection}>
          <p className="p3">{organization?.Contacts?.Address}</p>
          {location && <p className="p3">{location}</p>}
          {currentCountry && <p className="p3">{currentCountry}</p>}
          <p className="p3">{organization?.Contacts?.Phones[0]}</p>
          <p className="p3">{organization?.Contacts?.Emails[0]}</p>
          <p className="p3">{organization?.Contacts?.WebSite}</p>
        </div>
      </div>
    </div>
  );
};
